import request from '../request.js';


const filePurposeMap = ['bid_document', 'bid_user', 'bid_product', 'history_tender'];

/**
 * 文件上传API
 * @param file 文件对象
 * @param filePurpose 文件用途
 * @param onUploadProgress 上传进度回调函数
 * @returns Promise 上传结果
 */
export const uploadFile = async (file: File, filePurposeIndex: number, onUploadProgress?: (progressEvent: any) => void): Promise<any> => {
  try {
    const filePurpose = filePurposeMap[filePurposeIndex];
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filePurpose', filePurpose);
    
    const result = await request.post('/client/tender/cli_file/upload', formData, {
      retry: 1,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    });
    
    return result;
  } catch (error) {
    console.error('文件上传失败:', error);
    throw error;
  }
};

// 判断URL是否为本地URL（blob或data URL）
const isLocalUrl = (url) => {
  return url && (typeof url === 'string') && (url.startsWith('blob:') || url.startsWith('data:'));
};

// 判断是否为已上传的URL链接
const isUploadedUrl = (url) => {
  return url && typeof url === 'string' && !isLocalUrl(url) && url.startsWith('http');
};

// 从本地URL获取文件对象
const getFileFromLocalUrl = async (url, defaultFilename = 'upload.jpg') => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    
    let filename = defaultFilename;
    
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      const timestamp = Date.now();
      const extension = blob.type.split('/')[1] || 'jpg';
      filename = `local_upload_${timestamp}.${extension}`;
    }
    return new File([blob], filename, { type: blob.type });
  } catch (error) {
    console.error('从本地URL获取文件失败:', error);
    throw error;
  }
};

const _upload = async (file, filePurposeIndex, onUploadProgress) => {
  // 为每个文件创建包装的进度回调，添加上文件信息
  const fileProgressCallback = onUploadProgress ? (progressEvent: any) => {
    // 添加上文件信息到进度事件对象中
    const enhancedProgressEvent = {
      ...progressEvent,
      fileInfo: {
        name: file.name,
        size: file.size,
        type: file.type,
        uid: file.uid || `file_${file.name}_${Date.now()}`
      }
    };
    onUploadProgress(enhancedProgressEvent);
  } : undefined;
  
  const result = await uploadFile(file, filePurposeIndex, fileProgressCallback);
  
  // 返回包含文件信息的结果，便于用户区分不同类型的文件
  return {
    url: result.data,
    file,
    filePurpose: filePurposeMap[filePurposeIndex],
    result,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size
  };
};


/**
 * 批量上传文件
 * @param files 文件数组
 * @param filePurpose 文件用途
 * @param onUploadProgress 上传进度回调函数
 * @returns Promise 带文件信息的上传结果数组
 */
export const batchUploadFiles = async (files: any, filePurposeIndex: number, onUploadProgress?: (progressEvent: any) => void) => {
  try {
    return await Promise.all(
      files.map(async (file) => {
        if (!file) {
          return {
            url: '',
            file: file,
          };
        }
        // 情况1: 已上传的URL链接，直接返回
        if (isUploadedUrl(file)) {
          return {
            url: file,
            file: file,
          };
        }
        
        // 情况2: File对象，直接上传
        if (file instanceof File) {
          const _res = await _upload(file, filePurposeIndex, onUploadProgress);
          return _res;
        }
        
        // 情况3: 本地URL，先获取文件再上传
        if (isLocalUrl(file)) {
          try {
            const _file = await getFileFromLocalUrl(file);
            const _res = await _upload(_file, filePurposeIndex, onUploadProgress);
            return _res;
          } catch (error) {
            console.error('处理本地URL失败:', error);
            return {
              url: null,
              file: file,
              error: '本地文件处理失败'
            };
          }
        }
        
        return {
          url: null,
          file: file,
          error: '上传失败'
        };
      })
    );
  } catch (error) {
    console.error('批量文件上传失败:', error);
    throw error;
  }
};

/**
 * 上传对象中的图片字段
 * @param obj 包含图片值的对象（值可能为空、字符串或file文件）
 * @param filePurposeIndex 文件用途索引
 * @param imageFieldNames 可选的图片字段名称数组，如果不提供则自动检测
 * @param onUploadProgress 上传进度回调函数
 * @returns Promise 包含上传后URL的新对象
 */
export const uploadObjectImages = async (
  obj: Record<string, any>,
  filePurposeIndex: number,
  imageFieldNames?: string[],
  onUploadProgress?: (progressEvent: any) => void
): Promise<Record<string, any>> => {
  try {
    // 如果提供了图片字段名称数组，使用它；否则自动检测可能包含图片的字段
    let fieldsToCheck: string[];
    if (imageFieldNames && imageFieldNames.length > 0) {
      fieldsToCheck = imageFieldNames;
    } else {
      // 自动检测：获取对象中所有可能包含图片的字段
      fieldsToCheck = Object.keys(obj);
    }
    
    // 收集所有指定的图片字段
    const imageFields = fieldsToCheck.map(field => ({ field, value: obj[field] }));
    
    // 创建对象的深拷贝，避免修改原始对象
    const resultObj = {...obj};
    
    // 批量上传所有指定字段的值
    const uploadResults = await batchUploadFiles(
      imageFields.map(item => item.value),
      filePurposeIndex,
      onUploadProgress
    );
    
    // 更新结果对象中的字段值
    uploadResults.forEach((result, index) => {
      if (result.url) {
        resultObj[imageFields[index].field] = result.url;
      }
    });
    
    return resultObj;
  } catch (error) {
    console.error('上传对象图片失败:', error);
    throw error;
  }
};

export const uploadArrayFiles = async (
  array,
  filePurposeIndex: number,
  onUploadProgress?: (progressEvent: any) => void
) => {
  try {
    if (!array || !array.length) return [];
    
    // 批量上传所有指定字段的值
    const uploadResults = await batchUploadFiles(
      array.map(item => item.raw || item.url || item),
      filePurposeIndex,
      onUploadProgress
    );
    
    return uploadResults.map(result => result.url);
  } catch (error) {
    console.error('上传对象图片失败:', error);
    throw error;
  }
};


/**
 * 根据字典类型查询字典数据信息API
 */
export const getDicts = async (dictType: string) => {
  try {
    const result = await request.get('/client/dict/data/list', {
      pageNum: 1,
      pageSize: 99,
      dictType
    }, {
      retry: 1,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
    });
    
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * 图片OCR识别接口
 * @param imageUrl 图片URL
 * @param orcKey OCR类型关键字（idcardpositive,idcardback,businesslicense,qualification_certificate,deposit_account）
 * @returns Promise OCR识别结果
 */
export const getImageInfoByOCR = async (data) => {
  try {
    const result = await request.post('/client/orc/getImageInfo', data, {
      retry: 1
    });
    
    return result;
  } catch (error) {
    console.error('图片OCR识别失败:', error);
    throw error;
  }
};